import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetInspectionPoints } from '../../../store/masterData/Actions';
import { formatDateTime, isNullOrWhiteSpace, upsertArrayElement } from '../../../services/appFunctionsService';
import InspectionPointForm from './form/InspectionPointForm';
import { IInspectionPoint, InspectionPoint } from '../../../@types/model/masterData/inspectionPoint/inspectionPoint';
import InspectionPointHttpService from '../../../services/http/masterData/inspectionPointHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { InspectionPointFormValues, IInspectionPointFormValues } from '../../../@types/model/masterData/inspectionPoint/inspectionPointFormValues';
import { useLocation } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { IApiError } from '../../../@types/other';
import { setInspectionPointMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';

const InspectionPointScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const inspectionPoints = useAppSelector(x => x.masterData.inspectionPoints);
    const auth = useAppSelector(x => x.auth);
    const pathname = useLocation().pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedInspectionPoint, setSelectedInspectionPoint] = useState<IInspectionPoint>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingInspectionPoint, setDeletingInspectionPoint] = useState<IInspectionPoint>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await InspectionPointHttpService.getInspectionPointData();
                    dispatch(dataSetInspectionPoints(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading inspection points.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = useCallback(() => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setIsDialogOpen(false);
        setSelectedInspectionPoint(undefined);
        setFormGuid(undefined);
    }, []);

    const editInspectionPoint = useCallback((inspectionPoint : IInspectionPoint) => {
        setSelectedInspectionPoint(inspectionPoint);
        setFormGuid(inspectionPoint.guid);
        setIsDialogOpen(true);
    }, []);

    const onSubmit = async (values : IInspectionPointFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        if (isNullOrWhiteSpace(payload.guid)) {
            payload.guid = formGuid ?? '';
        }

        try {
            const res = await InspectionPointHttpService.addOrUpdateInspectionPoint(InspectionPoint(payload));
            const updatedList = upsertArrayElement(inspectionPoints, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetInspectionPoints(updatedList));
            await setInspectionPointMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedInspectionPoint ? 'Inspection point updated successfully.' : 'Inspection point added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating inspection point data.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteConfirmationPopup = useCallback((inspectionPoint : IInspectionPoint) => {
        setDeletingInspectionPoint(inspectionPoint);
        setIsDeletePopupOpen(true);
    }, []);

    const closeDeleteConfirmationPopup = useCallback(() => {
        setDeletingInspectionPoint(undefined);
        setIsDeletePopupOpen(false);
    }, []);

    const removeInspectionPoint = async () => {
        if (!deletingInspectionPoint) return;
        const updatedInspectionPoint = { ...deletingInspectionPoint, isActive: false };
        setIsLoading(true);
        try {
            const res = await InspectionPointHttpService.deleteInspectionPoint(updatedInspectionPoint.id);
            const updatedList = upsertArrayElement(inspectionPoints, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetInspectionPoints(updatedList));
            await setInspectionPointMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Inspection point deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting inspection point.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return InspectionPointFormValues(selectedInspectionPoint);
        }
    }, [selectedInspectionPoint, isDialogOpen]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(x => pathname.includes(x.url) && x.isActive && x.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    return (
        <Screen isLoading={isLoading}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Inspection Point'
                    isEdit={!!selectedInspectionPoint}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    maxWidth={'md'}
                    fullWidth
                    onClose={closeDialog}>
                    <InspectionPointForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                        isLoading={isLoading}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IInspectionPoint>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editInspectionPoint}
                        enableEditing={hasEditingRight}
                        enableDeleting={inspectionPoint => inspectionPoint.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Booking Ref Nr', field: 'bookingReferenceNumber', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={inspectionPoints}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                        fitWidthToPage
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Inspection Point'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this inspection point?'
                onAccept={removeInspectionPoint}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default InspectionPointScreen;
